import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthContainerComponent } from '@app/core/containers/auth-container/auth-container.component';
import { ContentContainerComponent } from '@app/core/containers/content-container/content-container.component';
import { ErrorContainerComponent } from '@app/core/containers/error-container/error-container.component';
import { CleanupResolverService } from '@app/core/services/resolvers/cleanup-resolver.service';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { AuthGuard } from './core/guards/auth.guard';
import { MenuGuard } from './core/guards/menu.guard';
import { MenuResolverService } from './core/services/resolvers/menu-resolver.service';
import { platformConfigRoute } from './platform-config.routes';

const routes: Routes = [
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full'
  },
  {
    path: '',
    component: ContentContainerComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'home',
        loadChildren: () => import('./features/home/home.module').then(m => m.HomeModule)
      },
      {
        path: 'user',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['USR_BO_ACCESS_USER_MANAGEMENT'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/user/user.module').then(m => m.UserModule)
      },
      {
        path: 'profile',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['USR_BO_ACCESS_PROFILE_MANAGEMENT'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'device',
        loadChildren: () => import('./features/device/device.module').then(m => m.DeviceModule)
      },
      {
        path: 'flows',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['USR_BO_ACCESS_FLOWS_MANAGEMENT'], //TODO: create a right
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/flows/flows.module').then(m => m.FlowsModule)
      },
      {
        path: 'connector',
        canActivate: [NgxPermissionsGuard],
        data: {
          permissions: {
            only: ['USR_BO_ACCESS_FLOWS_MANAGEMENT'], //TODO: create a right
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/connector/connector.module').then(m => m.ConnectorModule)
      },
      {
        path: 'right',
        canActivate: [MenuGuard],
        loadChildren: () => import('./features/right/right.module').then(m => m.RightModule)
      },
      {
        path: 'scheduler',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['SCH_BO_ACCESS_TASK'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/scheduler/scheduler.module').then(m => m.SchedulerModule)
      },
      {
        path: 'parameter',
        // TODO: pending create parameter rights
        // canActivate: [NgxPermissionsGuard, MenuGuard],
        // data: {
        //   permissions: {
        //     only: [Permissions.PAR_BO_ACCESS],
        //     redirectTo: '/forbidden'
        //   }
        // },
        loadChildren: () => import('./features/parameter/parameter.module').then(m => m.ParameterModule)
      },
      {
        path: 'structure',
        canActivate: [NgxPermissionsGuard], // FIXME: Pending to add in MenuGuard and backend
        data: {
          permissions: {
            only: ['STR_BO_ACCESS'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/structure/structure.module').then(m => m.StructureModule)
      },
      {
        path: 'menu',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['MNU_BO_ACCESS'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/menu/menu.module').then(m => m.MenuModule)
      },
      {
        path: 'hierarchy',
        canActivate: [MenuGuard],
        loadChildren: () => import('./features/hierarchy/hierarchy.module').then(m => m.HierarchyModule)
      },
      {
        path: 'division',
        canActivate: [MenuGuard],
        loadChildren: () => import('./features/division/division.module').then(m => m.DivisionModule)
      },
      {
        path: 'decodeddata',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['STR_BO_ACCESS_DECODED_DATA'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/decoded-data/decoded-data.module').then(m => m.DecodedDataModule)
      },
      {
        path: 'order',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['ORD_BO_ACCESS_OrderManager'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/order/order.module').then(m => m.OrderModule)
      },
      {
        path: 'activity',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['ACT_BO_ACCESS_ACTIVITY_MANAGER'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/activity/activity.module').then(m => m.ActivityModule)
      },
      {
        path: 'salesconditions',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['SLC_BO_ACCESS_SALESCONDITION_MANAGER'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () =>
          import('./features/salesconditions/salesconditions.module').then(m => m.SalesConditionsModule)
      },
      {
        path: 'forms',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['FRM_BO_ACCESS_FORM_MANAGER'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/forms/forms.module').then(m => m.FormsModule)
      },
      {
        path: 'customer',
        loadChildren: () => import('./features/customer/customer.module').then(m => m.CustomerModule)
      },
      {
        path: 'support',
        loadChildren: () => import('./features/support/support.module').then(m => m.SupportModule)
      },
      {
        path: 'library',
        // canActivate: [NgxPermissionsGuard, MenuGuard],
        // data: {
        //   permissions: {
        //     only: ['FRM_BO_ACCESS_FORM_MANAGER'],
        //     redirectTo: '/forbidden'
        //   }
        // },
        loadChildren: () => import('./features/library/library.module').then(m => m.LibraryModule)
      },
      {
        path: 'agenda',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['AGD_BO_ACCESS_AGENDA'],
            redirectTo: '/forbidden'
          }
        },
        resolve: {
          clean: CleanupResolverService
        },
        loadChildren: () => import('./features/agenda/agenda.module').then(m => m.AgendaModule)
      },
      {
        path: 'tos',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['TOS_BO_ACCESS_MANAGER'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/tos/tos.module').then(m => m.TOSModule)
      },
      {
        path: 'faq',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['FAQ_BO_ACCESS_FAQ_MANAGER'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/faq/faq.module').then(m => m.FaqModule)
      },
      {
        path: 'onboarding',
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['OBD_BO_ACCESS_MANAGER'],
            redirectTo: '/forbidden'
          }
        },
        loadChildren: () => import('./features/onboarding/obd.module').then(m => m.OnboardingModule)
      },
      {
        path: 'test',
        loadChildren: () => import('./features/test/test.module').then(m => m.TestModule)
      },
      {
        path: 'components',
        loadChildren: () => import('./features/components/components.module').then(m => m.ComponentsModule)
      },
      {
        path: 'forbidden',
        loadChildren: () => import('./features/forbidden/forbidden.module').then(m => m.ForbiddenModule)
      },
      {
        path: 'license',
        loadChildren: () => import('./features/license/license.module').then(m => m.LicenseModule)
      },
      {
        path: 'exportimport',
        loadChildren: () => import('./features/exportimport/exportimport.module').then(m => m.ExportImportModule)
      },
      {
        path: 'analytics',
        loadChildren: () => import('./features/analytics/analytics.module').then(m => m.AnalyticsModule)
      },
      {
        path: 'imagerecognition',
        loadChildren: () => import('./features/irc/irc.module').then(m => m.IrcModule),
        canActivate: [NgxPermissionsGuard, MenuGuard],
        data: {
          permissions: {
            only: ['IRC_BO_ACCESS_AUDIT_LIST'],
            redirectTo: '/forbidden'
          }
        },
      },
      ...platformConfigRoute
    ],
    resolve: {
      menu: MenuResolverService
    }
  },

  {
    path: 'auth',
    component: AuthContainerComponent,
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  { path: 'not-found', component: ErrorContainerComponent },
  { path: '**', redirectTo: '/not-found', pathMatch: 'full' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: []
})
export class AppRoutingModule {}
